/**
 * Text encoding detection (§22, §25).
 *
 * A global file viewer receives text produced by decades of different tools.
 * A Korean `.txt` from 2003 is EUC-KR, a Japanese log is Shift_JIS, a Windows
 * export is UTF-16LE with a BOM, and everything modern is UTF-8. Guessing wrong
 * does not produce a subtle defect — it produces a screen of mojibake, and if
 * the user then edits and saves, it produces permanent data loss.
 *
 * The strategy, in order of decreasing certainty:
 *
 *   1. A BOM is definitive. Trust it.
 *   2. UTF-16 without a BOM has a recognisable NUL pattern.
 *   3. Strict UTF-8 validation rejects nearly all legacy text, because legacy
 *      byte sequences are almost never valid UTF-8 by accident.
 *   4. Only then guess among legacy encodings, by decoding with each and
 *      scoring how plausible the resulting characters look for that encoding.
 *
 * Step 3 carries most of the weight. UTF-8's structure makes false positives
 * vanishingly unlikely, so anything that validates is UTF-8 and we never reach
 * the guessing stage for modern files.
 */

export type Encoding =
  | 'utf-8'
  | 'utf-16le'
  | 'utf-16be'
  | 'utf-32le'
  | 'utf-32be'
  | 'euc-kr'
  | 'shift_jis'
  | 'gb18030'
  | 'big5'
  | 'windows-1252';

export type EncodingConfidence =
  /** A BOM said so, or the bytes cannot be anything else. */
  | 'certain'
  /** Strict validation passed and the content has distinguishing structure. */
  | 'high'
  /** Chosen by scoring among legacy candidates with a clear winner. */
  | 'medium'
  /** Fallback. The user should be offered an encoding override (§22). */
  | 'low';

export interface EncodingCandidate {
  readonly encoding: Encoding;
  readonly score: number;
  readonly valid: boolean;
}

export type LineEnding = 'lf' | 'crlf' | 'cr' | 'mixed' | 'none';

export interface EncodingDetection {
  readonly encoding: Encoding;
  readonly confidence: EncodingConfidence;
  readonly hasBom: boolean;
  /** Bytes to skip before decoding. */
  readonly bomLength: number;
  /** All candidates considered, strongest first — shown in the encoding picker. */
  readonly candidates: readonly EncodingCandidate[];
}

export interface DecodedText {
  readonly text: string;
  readonly detection: EncodingDetection;
  readonly lineEnding: LineEnding;
  /** True when the file's last line has no terminator; preserved on save. */
  readonly endsWithoutNewline: boolean;
}

/* ------------------------------------------------------------------ *
 * BOM
 * ------------------------------------------------------------------ */

interface BomMatch { readonly encoding: Encoding; readonly length: number }

function detectBom(bytes: Uint8Array): BomMatch | null {
  // UTF-32 must be tested before UTF-16: the UTF-32LE BOM starts with the
  // UTF-16LE BOM followed by two NULs, so the shorter test would match first.
  if (bytes.length >= 4) {
    if (bytes[0] === 0xff && bytes[1] === 0xfe && bytes[2] === 0x00 && bytes[3] === 0x00) {
      return { encoding: 'utf-32le', length: 4 };
    }
    if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0xfe && bytes[3] === 0xff) {
      return { encoding: 'utf-32be', length: 4 };
    }
  }
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { encoding: 'utf-8', length: 3 };
  }
  if (bytes.length >= 2) {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return { encoding: 'utf-16le', length: 2 };
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return { encoding: 'utf-16be', length: 2 };
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * UTF-16 without a BOM
 * ------------------------------------------------------------------ */

/**
 * UTF-16 text that is mostly Latin has a NUL in every other byte — at odd
 * offsets for little-endian, even for big-endian. No single-byte or CJK
 * multi-byte encoding produces that pattern, because NUL is not a legal byte
 * in any of them.
 */
function detectBomlessUtf16(bytes: Uint8Array): Encoding | null {
  const sample = bytes.subarray(0, Math.min(bytes.length, 4096));
  if (sample.length < 16) return null;

  let nulAtOdd = 0;
  let nulAtEven = 0;
  const pairs = Math.floor(sample.length / 2);
  for (let i = 0; i < pairs * 2; i += 2) {
    if (sample[i] === 0x00) nulAtEven++;
    if (sample[i + 1] === 0x00) nulAtOdd++;
  }

  // A clear majority on exactly one side, and near-absence on the other.
  const threshold = pairs * 0.3;
  if (nulAtOdd > threshold && nulAtEven < pairs * 0.05) return 'utf-16le';
  if (nulAtEven > threshold && nulAtOdd < pairs * 0.05) return 'utf-16be';
  return null;
}

/* ------------------------------------------------------------------ *
 * UTF-8 validation
 * ------------------------------------------------------------------ */

export interface Utf8Check {
  readonly valid: boolean;
  /** True when every byte is < 0x80 — valid UTF-8 but not evidence of it. */
  readonly asciiOnly: boolean;
  readonly multiByteSequences: number;
  /** A multi-byte sequence ran past the end of the buffer. */
  readonly truncatedAtEnd: boolean;
}

/**
 * Validate UTF-8 by hand rather than through `TextDecoder(fatal)`.
 *
 * We need the multi-byte count as well as validity: pure ASCII is *valid* UTF-8
 * but is equally valid EUC-KR, Shift_JIS and windows-1252, so it must not be
 * reported with the same confidence as text containing real UTF-8 sequences.
 * Overlong forms, surrogates and out-of-range code points are rejected, since
 * accepting them is a well-known source of security bugs.
 */
export function checkUtf8(bytes: Uint8Array): Utf8Check {
  let i = 0;
  let multiByteSequences = 0;
  let asciiOnly = true;

  const invalid = (): Utf8Check => ({
    valid: false, asciiOnly: false, multiByteSequences, truncatedAtEnd: false,
  });

  while (i < bytes.length) {
    const b0 = bytes[i]!;
    if (b0 < 0x80) { i++; continue; }

    asciiOnly = false;
    let needed: number;
    let codePoint: number;

    if (b0 >= 0xc2 && b0 <= 0xdf) { needed = 1; codePoint = b0 & 0x1f; }
    else if (b0 >= 0xe0 && b0 <= 0xef) { needed = 2; codePoint = b0 & 0x0f; }
    else if (b0 >= 0xf0 && b0 <= 0xf4) { needed = 3; codePoint = b0 & 0x07; }
    else return invalid();
    // 0xc0/0xc1 are overlong two-byte forms; 0xf5+ exceeds U+10FFFF; 0x80-0xbf
    // is a continuation byte with no lead. All fall through to invalid above.

    if (i + needed >= bytes.length) {
      // The sequence runs off the end. When the caller passed a fixed-size
      // sample of a longer file this is a cut, not a defect — reported
      // separately so detection does not misclassify valid UTF-8 as legacy
      // just because the 64 KiB boundary landed mid-character.
      return { valid: true, asciiOnly: false, multiByteSequences, truncatedAtEnd: true };
    }

    for (let k = 1; k <= needed; k++) {
      const b = bytes[i + k]!;
      if ((b & 0xc0) !== 0x80) return invalid();
      codePoint = (codePoint << 6) | (b & 0x3f);
    }

    // Overlong encodings and surrogates are invalid even though the byte
    // pattern parses.
    if (needed === 2 && codePoint < 0x800) return invalid();
    if (needed === 3 && codePoint < 0x10000) return invalid();
    if (codePoint >= 0xd800 && codePoint <= 0xdfff) return invalid();
    if (codePoint > 0x10ffff) return invalid();

    multiByteSequences++;
    i += needed + 1;
  }

  return { valid: true, asciiOnly, multiByteSequences, truncatedAtEnd: false };
}

/* ------------------------------------------------------------------ *
 * legacy scoring
 * ------------------------------------------------------------------ */

const LEGACY_CANDIDATES: readonly Encoding[] = ['euc-kr', 'shift_jis', 'gb18030', 'big5', 'windows-1252'];

function inRange(code: number, lo: number, hi: number): boolean {
  return code >= lo && code <= hi;
}

/**
 * Characters that exist only in simplified or only in traditional Chinese.
 *
 * Byte-level scoring cannot separate GB18030 from Big5: both decode Chinese
 * byte pairs into plausible ideographs, and the generic "looks like CJK" score
 * comes out identical, leaving the winner to be decided by array order — which
 * is luck, not detection. Comparing the decoded characters against these
 * disjoint sets is the standard way to break the tie, and it is decisive
 * because a text is written in one script or the other, never both.
 */
const SIMPLIFIED_ONLY = new Set([...'这个国说时会来东车马长门问题实现产业务经济发达应学关简汉语义记认见让边还进运远档为电脑网页图书号单团头买卖车专东丽乐习乡书买']);
const TRADITIONAL_ONLY = new Set([...'這個國說時會來東車馬長門問題實現產業務經濟發達應學關簡漢語義記認見讓邊還進運遠檔為電腦網頁圖書號單團頭買賣車專東麗樂習鄉書買']);

function scriptVariantBias(text: string, encoding: Encoding): number {
  if (encoding !== 'gb18030' && encoding !== 'big5') return 0;
  let simplified = 0;
  let traditional = 0;
  for (const ch of text) {
    if (SIMPLIFIED_ONLY.has(ch)) simplified++;
    else if (TRADITIONAL_ONLY.has(ch)) traditional++;
  }
  if (simplified === traditional) return 0;
  const dominant = simplified > traditional ? 'gb18030' : 'big5';
  const margin = Math.abs(simplified - traditional);
  return encoding === dominant ? margin * 8 : -margin * 8;
}

/**
 * Score decoded text for how much it looks like real prose in that encoding.
 *
 * Positive weight for the script the encoding exists to represent; negative
 * weight for replacement characters, private-use code points and stray control
 * characters, which are what mis-decoding actually produces.
 */
function scoreDecoded(text: string, encoding: Encoding): number {
  if (text.length === 0) return 0;

  let hangul = 0, kana = 0, ideograph = 0, latin = 0, ascii = 0;
  let bad = 0, privateUse = 0;

  for (const ch of text) {
    const c = ch.codePointAt(0)!;
    if (c === 0xfffd) { bad += 1; continue; }
    if (c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) { bad += 1; continue; }
    if (c < 0x80) { ascii++; continue; }
    if (inRange(c, 0xac00, 0xd7a3) || inRange(c, 0x1100, 0x11ff) || inRange(c, 0x3130, 0x318f)) hangul++;
    else if (inRange(c, 0x3040, 0x309f) || inRange(c, 0x30a0, 0x30ff)) kana++;
    else if (inRange(c, 0x4e00, 0x9fff) || inRange(c, 0x3400, 0x4dbf)) ideograph++;
    else if (inRange(c, 0x00a0, 0x024f)) latin++;
    else if (inRange(c, 0xe000, 0xf8ff)) privateUse++;
    else bad += 0.5;
  }

  const total = text.length;
  // Mis-decoding reliably produces replacement or control characters, so a
  // single one is heavily disqualifying relative to the text length.
  let score = ascii * 0.1 - bad * 10 - privateUse * 5;

  switch (encoding) {
    case 'euc-kr':
      score += hangul * 3 + ideograph * 0.5 - kana * 2;
      break;
    case 'shift_jis':
      // Kana are the strongest single signal in this set: no other candidate
      // produces them from plausible byte sequences.
      score += kana * 4 + ideograph * 1 - hangul * 3;
      break;
    case 'gb18030':
      // GB18030 decodes almost any byte sequence, so it needs real evidence
      // rather than merely surviving validation.
      score += ideograph * 1.5 - kana * 1 - hangul * 2;
      break;
    case 'big5':
      score += ideograph * 1.5 - kana * 1 - hangul * 2;
      break;
    case 'windows-1252':
      // Latin-1 range characters are cheap to produce by accident, so they
      // earn little; this is the fallback of last resort.
      score += latin * 0.3 - hangul * 2 - kana * 2 - ideograph * 2;
      break;
    default:
      break;
  }

  score += scriptVariantBias(text, encoding);
  return score / Math.max(1, total / 100);
}

/**
 * Strict decode, tolerating a sequence cut by the sample boundary.
 *
 * When the file is longer than the sniff window, the window can land in the
 * middle of a two-byte EUC-KR or Shift_JIS character. Without `allowTailTrim`
 * that would fail validation and the encoding would be misidentified for every
 * large legacy file — so up to three trailing bytes are dropped and retried,
 * but only when the caller knows the sample was cut.
 */
function tryDecode(bytes: Uint8Array, encoding: Encoding, allowTailTrim: boolean): string | null {
  const decoder = new TextDecoder(encoding, { fatal: true });
  const maxTrim = allowTailTrim ? 3 : 0;
  for (let trim = 0; trim <= maxTrim; trim++) {
    try {
      return decoder.decode(bytes.subarray(0, bytes.length - trim));
    } catch {
      // try a shorter tail
    }
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * public API
 * ------------------------------------------------------------------ */

/** How many bytes to inspect when guessing. Enough for a reliable signal. */
const SNIFF_BYTES = 64 * 1024;

/**
 * Detect the encoding of a text file.
 *
 * @param hint An encoding the caller has reason to prefer — a user override, or
 *   a charset declared by a container (an XML declaration, an HTML meta tag).
 *   Honoured when the bytes decode cleanly under it.
 */
export function detectEncoding(bytes: Uint8Array, hint?: Encoding): EncodingDetection {
  const bom = detectBom(bytes);
  if (bom) {
    return {
      encoding: bom.encoding,
      confidence: 'certain',
      hasBom: true,
      bomLength: bom.length,
      candidates: [{ encoding: bom.encoding, score: Number.POSITIVE_INFINITY, valid: true }],
    };
  }

  const sampled = bytes.length > SNIFF_BYTES;
  const sample = sampled ? bytes.subarray(0, SNIFF_BYTES) : bytes;

  const utf16 = detectBomlessUtf16(sample);
  if (utf16) {
    return {
      encoding: utf16, confidence: 'high', hasBom: false, bomLength: 0,
      candidates: [{ encoding: utf16, score: Number.POSITIVE_INFINITY, valid: true }],
    };
  }

  const utf8 = checkUtf8(sample);
  if (utf8.valid && utf8.multiByteSequences > 0) {
    return {
      encoding: 'utf-8', confidence: 'high', hasBom: false, bomLength: 0,
      candidates: [{ encoding: 'utf-8', score: Number.POSITIVE_INFINITY, valid: true }],
    };
  }

  if (utf8.valid && utf8.asciiOnly) {
    // Pure ASCII. Every candidate would decode it identically, so UTF-8 is the
    // right answer and the choice is genuinely certain — not a lucky guess.
    return {
      encoding: 'utf-8', confidence: 'certain', hasBom: false, bomLength: 0,
      candidates: [{ encoding: 'utf-8', score: Number.POSITIVE_INFINITY, valid: true }],
    };
  }

  // Not UTF-8. Score the legacy candidates.
  const candidates: EncodingCandidate[] = [];
  for (const encoding of LEGACY_CANDIDATES) {
    const decoded = tryDecode(sample, encoding, sampled);
    if (decoded === null) {
      candidates.push({ encoding, score: Number.NEGATIVE_INFINITY, valid: false });
      continue;
    }
    candidates.push({ encoding, score: scoreDecoded(decoded, encoding), valid: true });
  }
  candidates.sort((a, b) => b.score - a.score);

  if (hint) {
    const hinted = candidates.find((c) => c.encoding === hint);
    if (hinted?.valid) {
      return {
        encoding: hint, confidence: 'high', hasBom: false, bomLength: 0,
        candidates: [hinted, ...candidates.filter((c) => c !== hinted)],
      };
    }
  }

  const best = candidates[0];
  if (!best || !best.valid) {
    // Nothing decoded cleanly. windows-1252 maps every byte to something, so it
    // is the only choice that cannot fail — but the confidence says so.
    return {
      encoding: 'windows-1252', confidence: 'low', hasBom: false, bomLength: 0, candidates,
    };
  }

  const runnerUp = candidates.find((c) => c !== best && c.valid);
  // A clear margin over the next valid candidate means the winner is not
  // merely the least bad option.
  const decisive = !runnerUp || best.score > runnerUp.score * 1.5 || best.score - runnerUp.score > 20;
  // A negative score means the decoded text looks like nothing in particular —
  // the winner only won because everything else was worse. windows-1252 maps
  // every possible byte, so there is *always* a winner; saying "medium" about
  // one with a negative score would dress up a failure as a result.
  const plausible = best.score > 0;

  return {
    encoding: best.encoding,
    confidence: decisive && plausible ? 'medium' : 'low',
    hasBom: false,
    bomLength: 0,
    candidates,
  };
}

function detectLineEnding(text: string): { lineEnding: LineEnding; endsWithoutNewline: boolean } {
  const crlf = (text.match(/\r\n/g) ?? []).length;
  const lfTotal = (text.match(/\n/g) ?? []).length;
  const crTotal = (text.match(/\r/g) ?? []).length;
  const lf = lfTotal - crlf;
  const cr = crTotal - crlf;

  const kinds = [crlf > 0, lf > 0, cr > 0].filter(Boolean).length;
  const lineEnding: LineEnding = kinds === 0 ? 'none'
    : kinds > 1 ? 'mixed'
      : crlf > 0 ? 'crlf'
        : lf > 0 ? 'lf' : 'cr';

  const endsWithoutNewline = text.length > 0 && !/[\r\n]$/.test(text);
  return { lineEnding, endsWithoutNewline };
}

/**
 * Decode bytes to text, detecting the encoding first.
 *
 * The line-ending style is reported rather than normalised: §22 says saving
 * uses UTF-8, but silently rewriting every CRLF to LF would make a one-word
 * edit show up as a whole-file change in the user's version control, and would
 * corrupt files that genuinely require CRLF.
 */
export function decodeTextBytes(bytes: Uint8Array, hint?: Encoding): DecodedText {
  const detection = detectEncoding(bytes, hint);
  const payload = detection.bomLength > 0 ? bytes.subarray(detection.bomLength) : bytes;

  let text: string;
  try {
    text = new TextDecoder(detection.encoding).decode(payload);
  } catch {
    // A runtime lacking this legacy decoder — fall back rather than throw.
    text = new TextDecoder('windows-1252').decode(payload);
  }

  return { text, detection, ...detectLineEnding(text) };
}

/**
 * Encode text for saving.
 *
 * §22 mandates UTF-8 on save. The original line-ending style is restored so a
 * round trip through the editor is byte-identical when nothing was edited.
 */
export function encodeTextForSave(
  text: string,
  lineEnding: LineEnding,
  options: { readonly withBom?: boolean } = {},
): Uint8Array {
  let out = text;
  if (lineEnding === 'crlf') out = text.replace(/\r\n|\r|\n/g, '\r\n');
  else if (lineEnding === 'lf') out = text.replace(/\r\n|\r/g, '\n');
  else if (lineEnding === 'cr') out = text.replace(/\r\n|\n/g, '\r');
  // 'mixed' and 'none' are left exactly as the editor produced them.

  const body = new TextEncoder().encode(out);
  if (!options.withBom) return body;

  const withBom = new Uint8Array(body.length + 3);
  withBom.set([0xef, 0xbb, 0xbf], 0);
  withBom.set(body, 3);
  return withBom;
}
